import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import * as avatarApi from "./avatar-api";
import { avatarQueries } from "./avatar-keys";

/**
 * Hook to download and create object URL for avatar
 * Uses TanStack Query for caching downloaded blobs
 */
export function useAvatarQuery(storageName: string, path: string | null, enabled = true) {
  return useQuery({
    ...avatarQueries.download(storageName, path || ""),
    enabled: enabled && !!storageName && !!path,
    staleTime: 10 * 60 * 1000, // 10 minutes
    gcTime: 30 * 60 * 1000, // 30 minutes
  });
}

/**
 * Hook to get public URL for an avatar
 * This doesn't require downloading the file, just gets the public URL
 */
export function usePublicUrlQuery(storageName: string, path: string | null, enabled = true) {
  return useQuery({
    ...avatarQueries.publicUrl(storageName, path || ""),
    enabled: enabled && !!storageName && !!path,
  });
}

/**
 * Hook to get public URL for an avatar
 * This doesn't require downloading the file, just gets the public URL
 */
export function useSignedUrlQuery(storageName: string, path: string | null, enabled = true) {
  return useQuery({
    ...avatarQueries.signedUrl(storageName, path || ""),
    enabled: enabled && !!storageName && !!path,
  });
}

/**
 * Hook to upload avatar files
 * Automatically invalidates related queries
 */
export function useUploadAvatarMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: avatarApi.uploadAvatar,
    onSuccess: (storageName, filePath) => {
      // Invalidate the specific avatar query
      void queryClient.invalidateQueries({
        queryKey: avatarQueries.download(storageName, filePath.filePath).queryKey,
      });

      // Invalidate the public URL query
      void queryClient.invalidateQueries({
        queryKey: avatarQueries.publicUrl(storageName, filePath.filePath).queryKey,
      });

      // Invalidate all storage queries
      void queryClient.invalidateQueries({
        queryKey: avatarQueries.all(),
      });
    },
    onError: (error) => {
      console.error("Avatar upload failed:", error);
    },
  });
}

/**
 * Hook to delete avatar files
 * Automatically removes from cache
 */
export function useDeleteAvatarMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      storageName,
      filePath,
    }: {
      storageName: string;
      filePath: string;
    }) => avatarApi.deleteAvatar(storageName, filePath),
    onMutate: async ({
      storageName,
      filePath,
    }: {
      storageName: string;
      filePath: string;
    }) => {
      // Cancel outgoing refetches for this avatar
      await queryClient.cancelQueries({
        queryKey: avatarQueries.download(storageName, filePath).queryKey,
      });

      // Get current avatar URL to revoke it
      const currentUrl = queryClient.getQueryData<string>(
        avatarQueries.download(storageName, filePath).queryKey,
      );

      // Revoke the object URL to free memory
      if (currentUrl) {
        URL.revokeObjectURL(currentUrl);
      }

      // Remove from cache immediately
      queryClient.removeQueries({
        queryKey: avatarQueries.download(storageName, filePath).queryKey,
      });

      queryClient.removeQueries({
        queryKey: avatarQueries.publicUrl(storageName, filePath).queryKey,
      });

      return { filePath };
    },
    onSuccess: () => {
      // Invalidate all storage queries
      void queryClient.invalidateQueries({
        queryKey: avatarQueries.all(),
      });
    },
    onError: (error, { storageName, filePath }) => {
      console.error("Avatar delete failed:", error);

      // Invalidate the specific avatar query to refresh from server
      void queryClient.invalidateQueries({
        queryKey: avatarQueries.download(storageName, filePath).queryKey,
      });
    },
  });
}
